import { Link } from 'react-router-dom';
import { DEFAULT_ADDRESS, DEFAULT_LABEL } from '../lib/storage';
import styles from './LegalPage.module.css';

const SUPPORT_EMAIL = 'sales@reintslabs.com';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=WhaleTail%20support`;

export function SupportPage() {
  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Support</h1>
      <p className={styles.subheading}>
        Questions or problems with WhaleTail? Email Reints Labs LLC at the address below.
      </p>

      <section className={styles.section} aria-labelledby="support-contact">
        <h2 id="support-contact">Contact</h2>
        <p>
          Email <a href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</a> with &ldquo;WhaleTail
          support&rdquo; in the subject. It helps to include:
        </p>
        <ul>
          <li>The page you were on and what you were trying to do.</li>
          <li>What you expected to see, and what you saw instead.</li>
          <li>
            Your device and browser, and whether you opened WhaleTail from your home
            screen.
          </li>
          <li>
            A screenshot, including the source labels under the wallet balance if the
            problem is with a balance or price.
          </li>
        </ul>
        <p>
          Never include a private key, seed phrase or exchange login. WhaleTail never asks
          for them, and support never needs them.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="support-faq">
        <h2 id="support-faq">Common questions</h2>

        <h3>Why does the price say Coinbase instead of CoinGecko?</h3>
        <p>
          The BTC/USD price comes from CoinGecko. CoinGecko often blocks keyless requests
          from shared servers, so when it fails WhaleTail uses Coinbase&apos;s public
          BTC-USD price instead. The labels under the balance show which source answered.
          If neither answers, the page says USD pricing is unavailable; the BTC balance is
          still accurate.
        </p>

        <h3>Why hasn&apos;t a new transaction shown up yet?</h3>
        <p>
          The wallet refreshes about once a minute, and answers are briefly cached, so a
          new transaction can take a minute or more to appear. Unconfirmed transactions
          count in the &ldquo;Mempool adjustment&rdquo; until they confirm. The refresh
          control next to the Wallet heading checks again straight away. If a lookup fails
          or you&apos;re offline, WhaleTail shows the last data it loaded and says so.
        </p>

        <h3>Which address is tracked, and can I change it?</h3>
        <p>
          By default WhaleTail tracks <code>{DEFAULT_ADDRESS}</code>, labeled &ldquo;
          {DEFAULT_LABEL}&rdquo;. The label is not a verified identity. You can track any
          valid Bitcoin mainnet address in <Link to="/settings">Settings</Link>, and reset
          to the default there. Your choice is saved only in this browser, so another
          browser or device starts with the default.
        </p>

        <h3>Is the market signal telling me to buy or sell?</h3>
        <p>
          No. The EMA/RSI signal is an informational calculation from past prices. It is
          backward-looking and can be wrong. The{' '}
          <Link to="/methodology">methodology</Link> explains how it works.
        </p>

        <h3>Why does the Scriptable widget say STALE?</h3>
        <p>
          The companion Scriptable widget asks WhaleTail first, then the public blockchain
          and price sources directly. If both fail, it shows its last saved result marked
          STALE. iOS decides how often widgets refresh, usually every 15 minutes or more.
        </p>

        <h3>What does Rug Check cover?</h3>
        <p>
          <Link to="/rug-check">Rug Check</Link> looks up a token on Ethereum, BNB Chain,
          Base, Arbitrum, Polygon or Solana and reports red flags found by GoPlus Security
          and DEX Screener. It can find warning signs but can&apos;t tell you a token is
          safe, and it never touches a wallet.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="support-notes">
        <h2 id="support-notes">Good to know</h2>
        <p>
          WhaleTail is read-only research and <strong>not</strong> financial, investment,
          or betting advice. It never requests private keys, seed phrases, wallet
          signatures, or exchange credentials, and it never places trades. See the{' '}
          <Link to="/terms">Terms of Use</Link> and the{' '}
          <Link to="/privacy">Privacy Policy</Link> for details.
        </p>
      </section>
    </div>
  );
}
